import type { BootState } from 'owa-account-source-list-store';
import {
    getGlobalSettingsAccountMailboxInfo,
    tryGetGlobalSettingsAccountMailboxInfo,
    setAdditionalMailboxPropsFromSessionSettings,
    setCoprincipalAccountBootState,
    setCoprincipalAccountContractUpdateCallback,
} from 'owa-account-source-list-store';
import { analyticsSettingsFlightsAndEcsSettingsAddon } from 'owa-analytics-addon-analytics-flights-and-ecs-settings';
import { registerAnalyticsAddon } from 'owa-analytics-addons';
import type { AnalyticsOptions } from 'owa-analytics-types';
import { logStartUsage } from 'owa-analytics-start';
import {
    initializeApplicationSettings,
    overrideDefaultApplicationSettings,
} from 'owa-application-settings';
import { parseExtraSettings } from 'owa-config';
import { getNativeHostVersion } from 'owa-config/lib/enhancedHost';
import { setLogicalRing } from 'owa-config/lib/getLogicalRing';
import {
    extractFlightsFromSettings,
    initializeFeatureFlags,
    isFeatureEnabled,
} from 'owa-feature-flags';
import { getLocalStorageOverrides } from 'owa-feature-flags/lib/utils/overrides/localStorageOverrides';
import { createMailboxInfoForUnifiedInboxWithMailboxInfos } from 'owa-mailbox-info/lib/createMailboxInfoForUnifiedInboxWithMailboxInfos';
import type OwaUserConfiguration from 'owa-service/lib/contract/OwaUserConfiguration';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import { setAccountConfiguration } from 'owa-session-store/lib/actions/setAccountConfiguration';
import type { WorkerBootstrapOptions } from './types/WorkerBootstrapOptions';
import { syncMailboxCollectorUrlMapping } from 'owa-analytics-utils/lib/syncMailboxCollectorUrlMapping';

/**
 * Monarch-correct default values for application settings that ECS normally overrides
 * for Platform == OutlookNativeHost. When ECS fails, these ensure Monarch boots with
 * correct platform identity and features instead of degrading to web behavior.
 *
 * - MonarchNotifications: enables desktop notifications, reminders, activity feed (ECS 1012092)
 * - PlatformType: core "am I Monarch?" flag used in 50+ files for telemetry and capabilities
 * - ExtensibilityPlatformType: controls add-in platform communication with native host
 * - ForceReboot: uses native host restart instead of page reload
 *
 */
const monarchDefaults: Record<string, Record<string, unknown>> = {
    MonarchNotifications: { enabled: true },
    PlatformType: { isMonarch: true },
    ExtensibilityPlatformType: { isMonarch: true },
    ForceReboot: { NativeForceRebootEnabled: true, WebForceRebootEnabled: false },
};

/**
 * Performs bootstrap initialization of stores based on the data returned from the
 * StartupData call. This function is named V1 because the next planned change is
 * to add support for passing in MailboxInfo rather than sourceId
 * @param sessionData SessionData for the coprincipal account returned by the StartupData call
 * @param sourceId Identifies the coprincipal account
 * @param bootState Boot state to which the account will be set
 * @param analyticsOptions Options which will be used for logging analytics
 */
export default function initializeStartupDataV1(
    sessionData: SessionData,
    sourceId: string,
    bootState: BootState,
    analyticsOptions?: AnalyticsOptions,
    workerOptions?: WorkerBootstrapOptions
): void {
    const extraSettings = parseExtraSettings(sessionData.extraSettings);
    const featureOverrides: string[] = extraSettings.featureOverrides || [];
    const featureFlags = (sessionData.features || []).concat(featureOverrides);
    if (analyticsOptions) {
        analyticsOptions.defaultFeatureFlags = featureFlags;
    }

    const flightsFromSettings = extractFlightsFromSettings(
        sessionData.applicationSettings?.settings
    );
    const logicalRing = initializeFeatureFlags(
        [...featureFlags, ...flightsFromSettings],
        getLocalStorageOverrides()
    );
    setLogicalRing(logicalRing);

    if (isFeatureEnabled('acct-mbx-props')) {
        // Set the extra MailboxInfo properties for the global settings account
        setAdditionalMailboxPropsFromSessionSettings(
            sourceId,
            sessionData.owaUserConfig?.SessionSettings
        );
    }

    // updates the account contracts now that flights are initialised
    setCoprincipalAccountContractUpdateCallback(account =>
        account.sourceType === 'PstFile'
            ? {
                  supportsCalendar: true,
                  supportsContacts: true,
              }
            : undefined
    );

    Error.stackTraceLimit = 20;

    // Preload the worker if it is available
    // Needs to be called after feature flags are initialized
    workerOptions?.preload?.();

    // When running in Monarch (native host), override the default application settings
    // with Monarch-correct values BEFORE initialization. This way, if ECS fails to return
    // settings (config undefined, settings undefined, or partial data), the merge/fallback
    // paths in initializeApplicationSettings naturally use Monarch-correct defaults.
    // These match what ECS configs (e.g. 1012092) return for Platform == OutlookNativeHost.
    // See ADO #406920 for full context.
    if (getNativeHostVersion()) {
        overrideDefaultApplicationSettings(monarchDefaults);

        const settings = sessionData.applicationSettings?.settings;
        const fallbackGroups = Object.keys(monarchDefaults).filter(group => !settings?.[group]);

        if (fallbackGroups.length > 0) {
            logStartUsage(
                'MonarchEcsSettingsFallbackApplied',
                tryGetGlobalSettingsAccountMailboxInfo(),
                {
                    groups: fallbackGroups.join(','),
                    fullFallback: !settings,
                }
            );
        }
    }

    initializeApplicationSettings(sessionData.applicationSettings);
    // NOTE: make a copy of user configuration to modifications to original object
    const userConfig: OwaUserConfiguration = JSON.parse(JSON.stringify(sessionData.owaUserConfig));
    const globalSettingsAccountMailboxInfo = getGlobalSettingsAccountMailboxInfo();

    // Sync mailbox to collector URL mapping for analytics
    // Note: This is only for primary accounts, secondary accounts will have
    // their mailbox info in the initializeApplicationSettings call.
    const collectorUrl = sessionData.applicationSettings?.settings?.Analytics?.collectorUrl;
    syncMailboxCollectorUrlMapping(collectorUrl, globalSettingsAccountMailboxInfo);

    // Analytics events can be create from this point using features flags and app settings
    // so we need to make sure the settings are accessible to the analytics code
    registerAnalyticsAddon(
        'AnalyticsFlightsAndEcsSettings',
        analyticsSettingsFlightsAndEcsSettingsAddon
    );

    // NOTE: setAccountConfiguration needs to happen after initializeApplicationSettings to make sure the flights
    // and applications settings are initialized when userConfigurationSet is called
    setAccountConfiguration(
        'StartupData',
        globalSettingsAccountMailboxInfo,
        userConfig,
        true /* workaround for indexer values not always being unique: this is known in this context to be the global account */
    );

    if (isFeatureEnabled('acct-unifiedInbox')) {
        // We only have the primary account sourceId at this point, so we create a unified inbox
        // mailbox info with the global account mailbox info as the only account in the unified inbox.
        const unifiedInboxMailboxInfo = createMailboxInfoForUnifiedInboxWithMailboxInfos([
            globalSettingsAccountMailboxInfo,
        ]);
        setAccountConfiguration(
            'BootstrapUnifiedInbox',
            unifiedInboxMailboxInfo,
            {},
            false /* not the global settings account */
        );
    }

    setCoprincipalAccountBootState(sourceId, bootState);
}
